// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { SelectChangeEvent } from '@mui/material/Select';
import { routes } from '@seedplanner/shared-web';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, FormControl, InputLabel, MenuItem, Select } from '../../lib/base';
import { YouTubeIcon } from '../../lib/icons';

interface YouTubeEmbedProps {
  readonly src: string;
  readonly title: string;
}

function YouTubeEmbed({ src, title }: YouTubeEmbedProps) {
  // Inline styles for the wrapper and iframe for responsiveness
  const wrapperStyle = {
    position: 'relative' as const,
    overflow: 'hidden',
    width: '100%',
    paddingTop: '56.25%', // Aspect ratio: 100 / (16/9) = 56.25%
  };

  const iframeStyle = {
    position: 'absolute' as const,
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
  };

  return (
    <div style={wrapperStyle}>
      <iframe
        style={iframeStyle}
        src={src}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

interface Video {
  readonly id: string;
  readonly title: string;
}

const videos: Video[] = [
  { title: 'Create Your Account & A Software Orientation', id: 'https://www.youtube.com/embed/YQx536tyipk' },
  { title: 'Create Default Crops', id: 'https://www.youtube.com/embed/hRAwxY1-o2I' },
  { title: 'Create Custom Crops', id: 'https://www.youtube.com/embed/ApqheaF5nPM' },
  { title: 'Editing Crops', id: 'https://www.youtube.com/embed/V1DhP00n-VE' },
  { title: 'Create Blends', id: 'https://www.youtube.com/embed/pz96PivHa7M' },
  { title: 'Editing Blends', id: 'https://www.youtube.com/embed/kNkWn_NwQqc' },
  { title: 'How to Archive Crops & Blends', id: 'https://www.youtube.com/embed/Vl2L03u5XsI' },
  { title: 'Create One-Off Orders & Order Details', id: 'https://www.youtube.com/embed/n97cb_XHkRk' },
  { title: 'Canceling One-Off Order', id: 'https://www.youtube.com/embed/qBX2Ud3SY-U' },
  { title: 'Editing One-Off Orders - Changing the Delivery Date', id: 'https://www.youtube.com/embed/jK0JH3EC4S4' },
  { title: 'Editing One-Off Orders - Reducing Product Volumes', id: 'https://www.youtube.com/embed/88vZavaTQOY' },
  {
    title: 'Editing One-Off Orders - Increasing Volume & Changing the Products',
    id: 'https://www.youtube.com/embed/I-aauBixqiM',
  },
  { title: 'How is Tray Count Calculated', id: 'https://www.youtube.com/embed/x225O2u4EY8' },
  { title: 'How Microgreen Manager Creates Tasks via Orders', id: 'https://www.youtube.com/embed/PFhxLY1MJlM' },
  { title: 'Marking Anything "Complete"', id: 'https://www.youtube.com/embed/9sopSyc0ezc' },
  {
    title: 'Status Types - Planned, In Progress, Complete, Cancelled',
    id: 'https://www.youtube.com/embed/ig9Whr0lyLU',
  },
  { title: 'Create One-Off Tasks', id: 'https://www.youtube.com/embed/Ypu98ErVsmQ' },
  { title: 'Everything for Recurring Tasks', id: 'https://www.youtube.com/embed/71ckmKiWDmE' },
  { title: 'Orientation to a Harvest Page', id: 'https://www.youtube.com/embed/uZqXwk-BQ38' },
  { title: 'Create Recurring Orders', id: 'https://www.youtube.com/embed/zDoUL1sqS6c' },
  {
    title: 'Faster Onboarding - Create "In Progress" Recurring Orders',
    id: 'https://www.youtube.com/embed/UkgOS45BfVM',
  },
  { title: 'How Recurring Orders Work - Behind the Scenes', id: 'https://www.youtube.com/embed/mX9ev8FF9fQ' },
  { title: 'Skipping Recurring Orders', id: 'https://www.youtube.com/embed/zbjnOtMy_c0' },
  { title: 'Deleting Recurring Orders', id: 'https://www.youtube.com/embed/tQ_L_-MIqi0' },
  { title: 'Deleting All Data & Deleting Your Account', id: 'https://www.youtube.com/embed/ukxjv9xNdk4' },
  {
    title: 'Every-Other-Week Orders: Shifting the "ON" & "OFF" Weeks',
    id: 'https://www.youtube.com/embed/ZBOBgpbyRsQ',
  },
  { title: 'Be Efficient: Batch Prepare Seeds & Trays for Next Week', id: 'https://www.youtube.com/embed/hgwnIPSuaaQ' },
  { title: 'Inviting Team Members & Changing Their Permissions', id: 'https://www.youtube.com/embed/aoKKwkGYDRA' },
  { title: 'Editing Recurring Orders', id: 'https://www.youtube.com/embed/WqRUzIr-OOY' },
  { title: 'Crop Buffers: Enhancing Crop Management', id: 'https://www.youtube.com/embed/6T24DZJtdbU' },
  { title: 'Bulk Importing Crops to Save Time!', id: 'https://www.youtube.com/embed/xeGNDyqlcdY' },
  { title: routes.PRICE_CALCULATOR_VIDEO, id: 'https://www.youtube.com/embed/kszhG8rp7lg' },
];

const VideoGrid: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Get possible video title from URL
  const videoParam = searchParams.get('video');
  // Decode the URL param
  const decodedVideoParam = videoParam ? decodeURIComponent(videoParam) : undefined;
  // Find the video object from the title
  const maybeFoundVideo = videos.find((video) => video.title === decodedVideoParam);
  const inputVideo = maybeFoundVideo ?? videos[0];
  const [selectedVideo, setSelectedVideo] = useState<Video>(inputVideo);
  const handleChange = (event: SelectChangeEvent<string>) => {
    const videoTitle = event.target.value as string;
    const video = videos.find((v) => v.title === videoTitle);
    if (video) {
      setSelectedVideo(video);
      setSearchParams({ video: video.title });
    }
  };

  // If there is no video found on the first load, then set the URL to the first video
  React.useEffect(() => {
    if (!maybeFoundVideo) {
      setSearchParams({ video: inputVideo.title });
    }
  }, [inputVideo, maybeFoundVideo, setSearchParams]);

  return (
    <Box sx={{ minWidth: 120 }}>
      <FormControl fullWidth>
        <InputLabel id="video-selector-label">Select Video</InputLabel>
        <Select
          labelId="video-selector-label"
          value={selectedVideo.title}
          label="Select Video"
          onChange={handleChange}
          startAdornment={<YouTubeIcon sx={{ color: 'red', mr: 2 }} />}
        >
          {videos.map((video) => (
            <MenuItem key={video.title} value={video.title}>
              {video.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box mt={2}>
        <YouTubeEmbed src={selectedVideo.id} title={selectedVideo.title} />
      </Box>
    </Box>
  );
};

export default VideoGrid;
